import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { poStatusLabel } from './poStatus';
import './ProcurementOverview.css';

function ProcurementOverview() {
  const { t } = useTranslation('procurement');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    purchase_orders: { total: 0, draft: 0, sent: 0, confirmed: 0, received: 0, overdue: 0, created_last_30_days: 0 },
    providers: { total: 0, active: 0 },
    spending: { total_value: 0, avg_value: 0, value_last_30_days: 0, value_this_month: 0 },
    inventory: { low_stock_count: 0 },
    top_providers: [],
    recent_orders: []
  });

  // Fetch procurement metrics
  useEffect(() => {
    fetchProcurementMetrics();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchProcurementMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchProcurementMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/procurement/metrics/overview'));
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching procurement metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'RECEIVED': return 'status-success';
      case 'PARTIALLY_RECEIVED': return 'status-info';
      case 'CONFIRMED': return 'status-info';
      case 'SENT': return 'status-warning';
      case 'DRAFT': return 'status-default';
      case 'CANCELLED': return 'status-error';
      default: return 'status-default';
    }
  };

  return (
    <div className="procurement-overview">
      {/* Key Metrics Cards - 2 rows of 3 */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">📦</span>
            <span className="kpi-label">{t('dashboard.cards.totalPOs')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.purchase_orders.total}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.last30Days', { count: metrics.purchase_orders.created_last_30_days })}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">⏳</span>
            <span className="kpi-label">{t('dashboard.cards.pendingApproval')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.purchase_orders.sent}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.draft', { count: metrics.purchase_orders.draft })}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">💰</span>
            <span className="kpi-label">{t('dashboard.cards.totalValue')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : formatCurrency(metrics.spending.total_value)}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.avg')}: {formatCurrency(metrics.spending.avg_value)}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">🏢</span>
            <span className="kpi-label">{t('dashboard.cards.activeProviders')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.providers.active}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.total', { count: metrics.providers.total })}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">⚠️</span>
            <span className="kpi-label">{t('dashboard.cards.lowStockItems')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.inventory.low_stock_count}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.materialAlerts')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">🚨</span>
            <span className="kpi-label">{t('dashboard.cards.overdueDeliveries')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.purchase_orders.overdue}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('dashboard.labels.pastExpectedDate')}</div>
          </div>
        </div>
      </div>

      {/* Recent Purchase Orders */}
      {!loading && metrics.recent_orders.length > 0 && (
        <div className="customers-analytics">
          <div className="section-header">
            <span className="section-icon">📋</span>
            <h3>{t('dashboard.sections.recentPurchaseOrders')}</h3>
          </div>
          <div className="customers-table">
            {metrics.recent_orders.map((order) => (
              <div key={order.po_number} className="customer-row">
                <div className="customer-details">
                  <span className="customer-name">{order.po_number}</span>
                  <span className="customer-type">{order.provider_name}</span>
                </div>
                <div className="orders-count">{formatCurrency(order.total_amount)}</div>
                <div className="revenue-amount">
                  <span className={`status-badge ${getStatusClass(order.status)}`}>
                    {poStatusLabel(t, order.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Providers */}
      {!loading && metrics.top_providers.length > 0 && (
        <div className="customers-analytics">
          <div className="section-header">
            <span className="section-icon">🏆</span>
            <h3>{t('dashboard.sections.topProvidersByValue')}</h3>
          </div>
          <div className="customers-table">
            {metrics.top_providers.map((provider) => (
              <div key={provider.provider_name} className="customer-row">
                <div className="customer-details">
                  <span className="customer-name">{provider.provider_name}</span>
                </div>
                <div className="orders-count">{t('dashboard.labels.ordersCount', { count: provider.order_count })}</div>
                <div className="revenue-amount">{formatCurrency(provider.total_value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProcurementOverview;
